<template>
  <apexchart
    :key="chartKey"
    height="100%"
    type="line"
    :options="chartOptions"
    :series="chartSeries"
  ></apexchart>
</template>

<script>
import { defaultChartOptions } from '@/utils/globalApexConfig';
import { formatCurrencyUSD, calcManualsNetPnl } from '@/utils/funcs.js';

export default {
  name: 'EvolPnlComparisonChart',
  props: {
    timeSeries: { type: Array, required: true },
    aggLabels:  { type: Array, required: true },
  },
  computed: {
    parsedTimeSeries() {
      const points = (this.timeSeries || []).map(({ index, m }) => {
        const totalPnl = Number(m?.pnl || 0);
        const netPnl = calcManualsNetPnl(m);
        return { index, totalPnl, netPnl };
      });

      const baselineNetPnl = points[0]?.netPnl || 0;

      return points.map((point) => {
        const deltaNetPnl = point.netPnl - baselineNetPnl;
        return {
          ...point,
          baselineNetPnl,
          deltaNetPnl,
          combined: point.totalPnl + deltaNetPnl,
        };
      });
    },

    summary() {
      const points = this.parsedTimeSeries;
      if (!points.length) {
        return { totalPnl: 0, deltaNetPnl: 0, combined: 0, startNet: 0, endNet: 0 };
      }
      const first = points[0];
      const last = points[points.length - 1];
      return {
        totalPnl: last.totalPnl,
        deltaNetPnl: last.deltaNetPnl,
        combined: last.combined,
        startNet: first.netPnl,
        endNet: last.netPnl,
      };
    },

    chartKey() {
      const n = this.parsedTimeSeries.length;
      const first = this.parsedTimeSeries[0];
      const last = this.parsedTimeSeries[n - 1];
      return `pnl-cmp-${n}-${first?.totalPnl ?? 0}-${last?.netPnl ?? 0}`;
    },

    chartSeries() {
      const totalPnlArray = [];
      const deltaNetArray = [];
      const combinedArray = [];

      this.parsedTimeSeries.forEach((item) => {
        const i = item.index;
        totalPnlArray.push([i, Number(item.totalPnl.toFixed(2))]);
        deltaNetArray.push([i, Number(item.deltaNetPnl.toFixed(2))]);
        combinedArray.push([i, Number(item.combined.toFixed(2))]);
      });

      return [
        { name: 'Total PNL', type: 'area', data: totalPnlArray },
        { name: 'Δ Net PnL', type: 'line', data: deltaNetArray },
        { name: 'Combinado', type: 'line', data: combinedArray },
      ];
    },

    chartOptions() {
      const self = this;
      const { totalPnl, deltaNetPnl, combined, startNet, endNet } = this.summary;
      return {
        ...defaultChartOptions,
        chart: {
          ...defaultChartOptions.chart,
          type: 'line',
          zoom: { enabled: false },
        },
        colors: ['#00E34A', '#3b82f6', '#a78bfa'],
        stroke: { curve: 'smooth', width: [2, 2, 2], dashArray: [0, 0, 5] },
        markers: { size: [0, 3, 0], hover: { size: 4 }, strokeWidth: 0 },
        fill: { opacity: [0.25, 1, 1] },
        title: {
          text: 'Total PNL vs Δ Net PnL',
          align: 'left',
          style: { color: '#f1f1f1', fontSize: '14px', fontWeight: 600 },
        },
        subtitle: {
          text: `Total ${this.fmtSigned(totalPnl)}  |  Net ${this.fmtUsd(startNet)} → ${this.fmtUsd(endNet)} (${this.fmtSigned(deltaNetPnl)})  |  Combinado ${this.fmtSigned(combined)}`,
          align: 'left',
          style: { color: '#9ca3af', fontSize: '12px' },
        },
        grid: {
          borderColor: '#333',
          padding: { right: 30, left: 15, top: 10 },
        },
        xaxis: { type: 'numeric', labels: { formatter: this.xAxisFormatter } },
        yaxis: {
          title: { text: 'USD' },
          labels: { formatter: formatCurrencyUSD },
        },
        annotations: {
          yaxis: [{ y: 0, borderColor: '#666', strokeDashArray: 4 }],
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: function({ dataPointIndex }) {
            return self.generateTooltipHtml(dataPointIndex);
          },
        },
      };
    },
  },
  methods: {
    xAxisFormatter(val) {
      return this.aggLabels[Math.floor(val)] || '';
    },
    fmtUsd(v) {
      const val = v || 0;
      const formatted = Math.abs(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return val < 0 ? `-$${formatted}` : `$${formatted}`;
    },
    fmtSigned(v) {
      const val = v || 0;
      const formatted = Math.abs(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return val >= 0 ? `+$${formatted}` : `-$${formatted}`;
    },
    generateTooltipHtml(dataPointIndex) {
      const item = this.parsedTimeSeries[dataPointIndex];
      if (!item) return '';

      const label = this.aggLabels[dataPointIndex] || '';
      const totalColor = item.totalPnl >= 0 ? '#4ade80' : '#f87171';
      const deltaColor = item.deltaNetPnl >= 0 ? '#4ade80' : '#f87171';
      const combinedColor = item.combined >= 0 ? '#4ade80' : '#f87171';

      return `
        <div style="background-color: #1e1e1e; color: #fff; padding: 10px 14px; border-radius: 6px; border: 1px solid #444; font-size: 12px; box-shadow: 0 4px 16px rgba(0,0,0,0.7); min-width: 260px;">
          <div style="font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #444; padding-bottom: 4px; color: #38bdf8;">
            📅 ${label}
          </div>
          <div style="line-height: 1.7;">
            <div>Total PNL: <b style="color: ${totalColor}">${this.fmtSigned(item.totalPnl)}</b></div>
            <div>Net PnL: <b>${this.fmtUsd(item.netPnl)}</b></div>
            <div>Δ Net PnL: <b style="color: ${deltaColor}">${this.fmtSigned(item.deltaNetPnl)}</b></div>
            <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #444;">
              Combinado: <b style="color: ${combinedColor}">${this.fmtSigned(item.combined)}</b>
            </div>
            <div style="margin-top: 4px; color: #9ca3af; font-size: 11px;">
              Net inicial: ${this.fmtUsd(item.baselineNetPnl)}
            </div>
          </div>
        </div>
      `;
    },
  },
};
</script>
